import { ProcessedImageData } from "./ProcessedImageData";

export const fixedRatio = 1.5;
export const jpegQuality = 0.95;


interface AvailableSizes<T> {
    large: T;
    medium: T;
    small: T;
}

type Box = {
    x: number;
    y: number;
    width: number;
    height: number;
}

class ImageDataCacheSet implements AvailableSizes<ImageData> {
    large: ImageData;
    medium: ImageData;
    small: ImageData;

    constructor(large: ImageData, small: ImageData, tiny: ImageData) {
        this.large = large;
        this.medium = small;
        this.small = tiny;
    }
}

export class ImageDataCache {
    constructor(public imageDatas: ImageDataCacheSet, public originalBlob: Blob, public thumbnailCanvas: HTMLCanvasElement) {
    }

}

export class ImageProcessor {

    constructor(public width: number, public allowPng: boolean) {
    }


    dataUrltoBlob(dataUrl: string) {
        var bytes = dataUrl.split(',')[0].indexOf('base64') >= 0 ?
            atob(dataUrl.split(',')[1]) :
            unescape(dataUrl.split(',')[1]);
        var mime = dataUrl.split(',')[0].split(':')[1].split(';')[0];
        var max = bytes.length;
        var ia = new Uint8Array(max);
        for (var i = 0; i < max; i++)
            ia[i] = bytes.charCodeAt(i);
        return new Blob([ia], { type: mime });
    };

    dataUrltoBase64(dataUrl: string) {
        return dataUrl.split(',')[1];
    };

    processImageFile(file: File): Promise<ProcessedImageData> {
        const saveThis = this;
        return new Promise(function (resolve, reject) {
            var fileReader = new FileReader();
            fileReader.onload = (e: ProgressEvent) => {
                const image = new Image();
                const fileResult = fileReader.result;
                if (typeof fileResult !== 'string') {
                    throw new Error('Invalid file result type');
                }
                image.onload = (ev: Event) => {
                    try {
                        if (fileResult.startsWith('data:image/png')) {
                            if (!saveThis.allowPng) throw new Error('PNG files not allowed');
                            const base64 = saveThis.dataUrltoBase64(fileResult);
                            resolve({ jpgBlob: null, image: image, base64, imageType: { mimeType: 'image/png', fileExtension: 'png' } });
                        } else {
                            saveThis.validateNewImage(image);
                            const desiredWidth = saveThis.width;
                            const desiredHeight = desiredWidth / fixedRatio
                            const canvasBox: Box = { x: 0, y: 0, width: desiredWidth, height: desiredHeight };
                            const cropBox = saveThis.getCropBox(image.width, image.height);
                            const canvas = document.createElement('canvas');
                            saveThis.setCanvasSideAndLoadImage(image, canvas, canvasBox, cropBox);
                            const dataUrl = canvas.toDataURL('image/jpeg');
                            const blob = saveThis.dataUrltoBlob(dataUrl);
                            const base64 = saveThis.dataUrltoBase64(dataUrl);
                            resolve({ jpgBlob: blob, image: image, base64, imageType: { mimeType: 'image/jpeg', fileExtension: 'jpg' } });
                        }
                    } catch (e) {
                        reject(e);
                    }
                };
                image.src = fileResult;
            };
            fileReader.readAsDataURL(file);
        });
    }

    /**
     * Returns a box with one of these two dimensions trimmed down to the correct ratio
     */
    getCropBox(width: number, height: number): Box {
        const suppliedRatio = width / height;
        if (suppliedRatio > fixedRatio) {
            // too wide
            const chopOff = width - (fixedRatio * height);
            return { x: chopOff / 2, y: 0, width: (fixedRatio * height), height };
        } else if (suppliedRatio < fixedRatio) {
            // too narrow
            const chopOff = height - (width / fixedRatio);
            return { x: 0, y: chopOff / 2, width, height: (width / fixedRatio) };
        }
        else {
            return { x: 0, y: 0, width, height };
        }
    }



    createNewCanvasFromBox(box: Box) {
        const canvas = document.createElement('canvas');
        canvas.width = box.width;
        canvas.height = box.height;
        return canvas;
    }

    createNewCanvasFromWidth(width: number) {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = width / fixedRatio;
        return canvas;
    }

    getContext(canvas: HTMLCanvasElement) {
        const context = canvas.getContext('2d');
        if (!context) {
            throw 'Could not get 2d context from canvas';
        }
        return context;
    }

    validateNewImage(imageElement: HTMLImageElement) {
        if (imageElement.width < this.width) {
            throw new Error(`Image not big enough, must be at least ${this.width}px wide`);
        }
        // if (imageElement.width < imageElement.height) {
        //     throw new Error(`Image must be landscape`);
        // }
    }

    loadImageIntoCanvas(imageElement: HTMLImageElement, canvas: HTMLCanvasElement) {
        const cropBox = this.getCropBox(imageElement.width, imageElement.height);
        canvas.width = cropBox.width;
        canvas.height = cropBox.height;
        var context = this.getContext(canvas);
        context.drawImage(imageElement, cropBox.x, cropBox.y, cropBox.width, cropBox.height, 0, 0, cropBox.width, cropBox.height);
        return canvas;
    }

    setCanvasSideAndLoadImage(imageElement: HTMLImageElement, canvas: HTMLCanvasElement, canvasBox: Box, sourceImageBox: Box) {
        canvas.width = canvasBox.width;
        canvas.height = canvasBox.height;
        var context = this.getContext(canvas);
        context.drawImage(imageElement, sourceImageBox.x, sourceImageBox.y, sourceImageBox.width, sourceImageBox.height, canvasBox.x, canvasBox.y, canvasBox.width, canvasBox.height);
        return canvas;
    }


    resizeCanvas(canvas: HTMLCanvasElement, width: number) {
        throw new Error('No longer supported');
        //return new HermiteResize().resample(canvas, width, width / fixedRatio, true);
    }

    getFullImageDataFromContext(context: CanvasRenderingContext2D, width: number) {
        return context.getImageData(0, 0, width, width / fixedRatio);
    }


    getBlob(canvas: HTMLCanvasElement) {
        return new Promise<Blob>((resolve, reject) => {
            canvas.toBlob((blob: Blob | null) => {
                if (blob == null)
                    reject('Could not get blob from canvas');
                else
                    resolve(blob);
            }, 'image/jpeg', jpegQuality);
        });
    }

    applyFilterToCanvas(canvas: HTMLCanvasElement, filter: (pixels: ImageData) => ImageData) {
        const context = this.getContext(canvas);
        const copiedImageData = context.getImageData(0, 0, canvas.width, canvas.height);
        var filtered = filter(copiedImageData);
        context.putImageData(filtered, 0, 0);
    }

    loadInto(imageData: ImageData, canvas: HTMLCanvasElement) {
        const context = this.getContext(canvas);
        canvas.width = imageData.width;
        canvas.height = imageData.height;
        context.putImageData(imageData, 0, 0);
        const copiedImageData = context.getImageData(0, 0, canvas.width, canvas.height);
        let filtered = copiedImageData;
        context.putImageData(filtered, 0, 0);
    }
}